'use client';

import { motion } from 'framer-motion';
import {
  Building2,
  Users,
  Calendar,
  MapPin,
  Star,
  Shield,
  Clock,
  CheckCircle,
  Award,
  Phone,
  Mail,
  MessageSquare,
  Briefcase,
  Globe,
  TrendingUp,
  Heart
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Image from 'next/image';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function CorporatePage() {
  const services = [
    {
      icon: <Building2 className="w-8 h-8 text-[#FFC400]" />,
      title: "Corporate Retreats",
      description: "Executive retreats and team building events in luxury villas with meeting facilities and recreational amenities."
    },
    {
      icon: <Users className="w-8 h-8 text-[#FFC400]" />,
      title: "Group Accommodations",
      description: "Large group bookings for conferences, seminars, and corporate events with dedicated support staff."
    },
    {
      icon: <Calendar className="w-8 h-8 text-[#FFC400]" />,
      title: "Event Planning",
      description: "Complete event coordination including catering, transportation, and entertainment arrangements."
    },
    {
      icon: <MapPin className="w-8 h-8 text-[#FFC400]" />,
      title: "Location Services",
      description: "Prime locations in Dubai's business districts with easy access to airports and major attractions."
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-8 h-8 text-[#FFC400]" />,
      title: "Dedicated Account Manager",
      description: "Personal account manager for seamless coordination and support throughout your stay."
    },
    {
      icon: <Clock className="w-8 h-8 text-[#FFC400]" />,
      title: "24/7 Concierge Service",
      description: "Round-the-clock assistance for any corporate needs or guest requests."
    },
    {
      icon: <Award className="w-8 h-8 text-[#FFC400]" />,
      title: "Premium Amenities",
      description: "High-speed WiFi, meeting rooms, business centers, and luxury facilities."
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-[#FFC400]" />,
      title: "Flexible Booking",
      description: "Customizable packages and flexible cancellation policies for corporate clients."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Mitchell",
      company: "Tech Innovations Ltd",
      role: "HR Director",
      content: "18Bricks provided exceptional service for our annual company retreat. The villa was perfect for our team of 25, and the dedicated support made everything seamless.",
      rating: 5
    },
    {
      name: "Ahmed Hassan",
      company: "Global Finance Group",
      role: "Operations Manager",
      content: "Outstanding corporate hospitality. The meeting facilities were top-notch, and our international clients were thoroughly impressed with the accommodations.",
      rating: 5
    },
    {
      name: "Maria Rodriguez",
      company: "Consulting Partners",
      role: "Managing Partner",
      content: "Professional service from start to finish. The team handled all our complex requirements and delivered beyond expectations.",
      rating: 5
    }
  ];

  const packages = [
    {
      name: "Executive Retreat",
      price: "From $2,500/night",
      features: [
        "Luxury villa accommodation",
        "Meeting room facilities",
        "Dedicated concierge",
        "Airport transfers",
        "Daily housekeeping",
        "Business center access"
      ],
      capacity: "Up to 20 guests"
    },
    {
      name: "Corporate Conference",
      price: "From $5,000/night",
      features: [
        "Multiple villa bookings",
        "Conference facilities",
        "Event coordination",
        "Catering services",
        "Transportation",
        "24/7 support"
      ],
      capacity: "Up to 100 guests"
    },
    {
      name: "Team Building",
      price: "From $1,800/night",
      features: [
        "Recreational facilities",
        "Team activities",
        "Group dining",
        "Entertainment options",
        "Photography services",
        "Custom packages"
      ],
      capacity: "Up to 15 guests"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden h-[60vh]">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black/70 z-10"></div>
          <img
            src="/images/businessbaydubai-402729-130928.jpg"
            alt="Dubai Business Bay"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="relative h-full z-20 max-w-7xl mx-auto flex items-center justify-center text-center">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Corporate & Group Bookings
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Elevate your business events with our premium corporate hospitality services.
              From executive retreats to large conferences, we provide exceptional accommodations and support.
            </p>
            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-[#FFC400] hover:bg-[#FFA500] text-white">
                Request Quote
              </Button>
              <Button size="lg" variant="outline">
                View Properties
              </Button>
            </div> */}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Corporate Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We specialize in providing comprehensive corporate hospitality solutions
              tailored to meet your business needs and exceed expectations.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <Card className="h-full shadow-lg border-0 hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-[#FFC400]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-4 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Why Choose 18Bricks for Corporate Events?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We understand the unique requirements of corporate clients and deliver
              exceptional service that reflects your company's standards.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="w-20 h-20 bg-[#FFC400]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Packages Section */}
      {/* <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Corporate Packages</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose from our carefully designed corporate packages or let us create
              a custom solution tailored to your specific requirements.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative"
              >
                <Card className="h-full shadow-xl border-0 hover:shadow-2xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold mb-2 text-gray-900">{pkg.name}</h3>
                      <p className="text-3xl font-bold text-[#FFC400] mb-2">{pkg.price}</p>
                      <Badge variant="secondary" className="bg-[#FFC400]/10 text-[#FFC400] border-[#FFC400]/20">
                        {pkg.capacity}
                      </Badge>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {pkg.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-[#FFC400] mr-3 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button className="w-full bg-[#FFC400] hover:bg-[#FFA500] text-white">
                      Get Quote
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* Testimonials Section */}
      {/* <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">What Our Corporate Clients Say</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what leading companies say about our corporate services.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
              >
                <Card className="h-full shadow-lg border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-[#FFC400] fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-6 italic">"{testimonial.content}"</p>
                    <div className="border-t pt-4">
                      <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                      <p className="text-sm text-[#FFC400] font-medium">{testimonial.company}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* Contact Section */}
      {/* <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Ready to Plan Your Corporate Event?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Contact our corporate team today to discuss your requirements and receive a personalized quote.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div
              variants={fadeInUp}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#FFC400]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-[#FFC400]" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Call Us</h3>
              <p className="text-gray-600 mb-2">+971 4 123 4567</p>
              <p className="text-sm text-gray-500">Mon-Fri 9AM-6PM GST</p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#FFC400]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#FFC400]" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Email Us</h3>
              <p className="text-gray-600 mb-2">corporate@18bricks.com</p>
              <p className="text-sm text-gray-500">Response within 2 hours</p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#FFC400]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-8 h-8 text-[#FFC400]" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Live Chat</h3>
              <p className="text-gray-600 mb-2">Available 24/7</p>
              <p className="text-sm text-gray-500">Instant support</p>
            </motion.div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button size="lg" className="bg-[#FFC400] hover:bg-[#FFA500] text-white mr-4">
              Request Corporate Quote
            </Button>
            <Button size="lg" variant="outline" className="border-[#FFC400] text-[#FFC400] hover:bg-[#FFC400] hover:text-white">
              Download Brochure
            </Button>
          </motion.div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FFC400] to-[#FFA500]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">
              Elevate Your Corporate Events with 18Bricks
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Experience the difference of premium corporate hospitality.
              Let us make your next business event unforgettable.
            </p>
            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-[#FFC400] hover:bg-gray-100">
                Start Planning Today
              </Button>
              <Button size="lg" variant="outline">
                Schedule Consultation
              </Button>
            </div> */}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
